import { useState } from "react";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";


const formatDateNl = (isoString) => {
  if (!isoString) {
    return null;
  }
  return new Date(isoString).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
};

const handleBillingError = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail === "billing_disabled") {
    toast.info("Smartly Premium is bijna beschikbaar — nog heel even geduld!");
    return;
  }
  if (detail === "billing_misconfigured") {
    toast.error("Er ging iets mis met de betaalconfiguratie. Probeer het later opnieuw.");
    return;
  }
  if (detail === "stripe_error") {
    toast.error("Stripe is tijdelijk niet bereikbaar. Probeer het zo opnieuw.");
    return;
  }
  toast.error(getApiError(error));
};

export const PremiumCard = () => {
  const { user } = useAuth();
  const [busyAction, setBusyAction] = useState(null);
  const subscription = user?.subscription || {};
  const isPremium =
    subscription.plan_tier === "premium" &&
    ["active", "trialing", "past_due"].includes(subscription.subscription_status);
  const renewalDate = formatDateNl(subscription.current_period_end);
  const cancelDate = formatDateNl(subscription.cancel_at);

  const startCheckout = async (plan) => {
    try {
      setBusyAction(plan);
      const response = await api.post("/billing/checkout-session", { plan });
      window.location.assign(response.data.checkout_url);
    } catch (error) {
      handleBillingError(error);
      setBusyAction(null);
    }
  };

  const openPortal = async () => {
    try {
      setBusyAction("portal");
      const response = await api.post("/billing/portal-session");
      window.location.assign(response.data.portal_url);
    } catch (error) {
      handleBillingError(error);
      setBusyAction(null);
    }
  };

  return (
    <section id="premium-card" className="rounded-[28px] border border-amber-200 bg-amber-50/60 p-4" data-testid="premium-card">
      <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-amber-700">
        <Sparkles className="h-4 w-4" />
        Smartly Premium
      </p>

      {isPremium ? (
        <div className="mt-3 space-y-3" data-testid="premium-active-state">
          <p className="text-sm font-semibold text-stone-900" data-testid="premium-status">
            Je gebruikt Smartly Premium.
            {subscription.subscription_status === "past_due" ? " (betaling mislukt, werk je betaalmethode bij)" : ""}
          </p>
          {cancelDate ? (
            <p className="text-sm text-stone-600" data-testid="premium-cancel-note">
              Je abonnement stopt op {cancelDate}.
            </p>
          ) : renewalDate ? (
            <p className="text-sm text-stone-600" data-testid="premium-renewal-note">
              Verlengt op {renewalDate}.
            </p>
          ) : null}
          <Button
            variant="outline"
            className="h-12 w-full rounded-full border-amber-300 bg-white text-stone-800 hover:bg-amber-50"
            onClick={openPortal}
            disabled={busyAction === "portal"}
            data-testid="premium-portal-button"
          >
            {busyAction === "portal" ? "Even bezig..." : "Beheer abonnement"}
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-3" data-testid="premium-free-state">
          <p className="text-sm text-stone-700" data-testid="premium-pitch">
            Upgrade naar Smartly Premium voor volledige weekplanning, een onbeperkte voorraadkast, slimmer boodschappen plannen en premium plannings­intelligentie.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              className="h-12 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"
              onClick={() => startCheckout("monthly")}
              disabled={busyAction !== null}
              data-testid="premium-monthly-button"
            >
              {busyAction === "monthly" ? "Even bezig..." : "€7,99 / maand"}
            </Button>
            <Button
              className="h-12 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"
              onClick={() => startCheckout("annual")}
              disabled={busyAction !== null}
              data-testid="premium-annual-button"
            >
              {busyAction === "annual" ? "Even bezig..." : "€59 / jaar"}
            </Button>
          </div>
          <p className="text-xs text-stone-500" data-testid="premium-footnote">
            Free blijft bruikbaar binnen de limieten. Premium is maandelijks opzegbaar.
          </p>
        </div>
      )}
    </section>
  );
};
